import React, { useEffect, useState } from "react";
import { Mail, CheckCircle2, XCircle, Loader2, Send, Settings2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

function Badge({ ok, label }) {
    return (
        <div className="flex items-center gap-2 text-sm">
            {ok ? (
                <CheckCircle2 className="w-4 h-4 text-green-700" />
            ) : (
                <XCircle className="w-4 h-4" style={{ color: "#dc3545" }} />
            )}
            <span className={ok ? "text-magni-navy" : "text-magni-muted"}>{label}</span>
            <span
                className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 ml-auto"
                style={{
                    background: ok ? "#e6f4ea" : "#fde6e6",
                    color: ok ? "#0d6b3f" : "#a02020",
                }}
            >
                {ok ? "OK" : "Mancante"}
            </span>
        </div>
    );
}

export default function AdminDiagnostics() {
    const [diag, setDiag] = useState(null);
    const [loadingDiag, setLoadingDiag] = useState(true);
    const [testEmail, setTestEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const refresh = async () => {
        setLoadingDiag(true);
        try {
            const r = await api.get("/admin/diagnostics");
            setDiag(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoadingDiag(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const sendTest = async () => {
        if (!testEmail) return;
        setSending(true);
        setLastResult(null);
        try {
            const r = await api.post("/admin/test/email", { to: testEmail });
            setLastResult({ ok: true, message: `Email inviata correttamente a ${r.data.to}. Controlla la casella (e spam) entro pochi minuti.` });
            toast.success("Email di test inviata");
        } catch (e) {
            const detail = formatApiError(e.response?.data?.detail);
            setLastResult({ ok: false, message: detail });
            toast.error("Test fallito");
        } finally {
            setSending(false);
        }
    };

    const allSmtpOk = diag?.smtp && diag.smtp.host_set && diag.smtp.user_set && diag.smtp.pass_set;

    return (
        <div data-testid="admin-diagnostics-page">
            <Toaster position="top-center" richColors />

            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Settings2 className="w-5 h-5 text-magni-navy" />
                    <span className="overline">Sistema</span>
                </div>
                <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                    Diagnostica
                </h1>
                <p className="text-magni-muted text-sm mt-2 max-w-2xl">
                    Verifica che la configurazione email sia corretta e prova l'invio di una email di test.
                </p>
            </div>

            {/* SMTP config status */}
            <div className="bg-white border border-magni-light p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <Mail className="w-5 h-5 text-magni-navy" />
                    <h2 className="font-serif-display text-2xl text-magni-navy">Configurazione SMTP</h2>
                </div>

                {loadingDiag ? (
                    <div className="flex items-center gap-2 text-magni-muted text-sm py-6">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Caricamento...
                    </div>
                ) : diag ? (
                    <>
                        <div className="space-y-3 mb-5">
                            <Badge ok={diag.smtp.host_set} label="SMTP_HOST (smtps.aruba.it)" />
                            <Badge ok={diag.smtp.port_set} label="SMTP_PORT (465)" />
                            <Badge ok={diag.smtp.user_set} label={`SMTP_USER (${diag.smtp.smtp_user_masked})`} />
                            <Badge ok={diag.smtp.pass_set} label="SMTP_PASS" />
                            <Badge ok={diag.smtp.sender_set} label={`SENDER_EMAIL (${diag.smtp.sender_masked})`} />
                            <Badge ok={diag.frontend_url_set} label="FRONTEND_URL" />
                            <Badge ok={diag.reminder_enabled} label="Reminder 24h abilitato" />
                            <Badge ok={diag.scheduler_running} label="Scheduler attivo" />
                        </div>
                        {!allSmtpOk && (
                            <div className="border-l-4 p-4 bg-red-50 text-sm" style={{ borderColor: "#dc3545" }}>
                                <strong style={{ color: "#dc3545" }}>Configurazione incompleta.</strong> Una o più
                                variabili SMTP non sono impostate. Le email non verranno inviate finché non vengono
                                configurate nell'ambiente di produzione.
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-magni-muted text-sm">Errore nel caricamento della diagnostica.</div>
                )}
            </div>

            {/* Send test email */}
            <div className="bg-white border border-magni-light p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <Send className="w-5 h-5 text-magni-navy" />
                    <h2 className="font-serif-display text-2xl text-magni-navy">Invia email di test</h2>
                </div>
                <p className="text-magni-muted text-sm mb-4 max-w-2xl">
                    Invia una email di prova per verificare che il server SMTP funzioni. Usa la tua email personale
                    (gmail, outlook, ecc.) per essere sicuro che arrivi davvero. Se l'invio fallisce, l'errore esatto
                    apparirà qui sotto.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
                    <input
                        type="email"
                        data-testid="diag-test-email-input"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="tua-email@gmail.com"
                        className="magni-input flex-1"
                        disabled={sending}
                    />
                    <button
                        data-testid="diag-send-test-btn"
                        onClick={sendTest}
                        disabled={sending || !testEmail}
                        className="magni-btn-primary disabled:opacity-40"
                        style={{ minWidth: 160 }}
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                                Invio...
                            </>
                        ) : (
                            "Invia test"
                        )}
                    </button>
                </div>

                {lastResult && (
                    <div
                        data-testid="diag-test-result"
                        className="mt-5 p-4 border-l-4 text-sm"
                        style={{
                            borderColor: lastResult.ok ? "#0d6b3f" : "#dc3545",
                            background: lastResult.ok ? "#e6f4ea" : "#fde6e6",
                            color: lastResult.ok ? "#0d6b3f" : "#a02020",
                        }}
                    >
                        <div className="font-semibold mb-1">
                            {lastResult.ok ? "Invio riuscito" : "Invio fallito"}
                        </div>
                        <div className="break-words font-mono text-xs leading-relaxed">{lastResult.message}</div>
                        {!lastResult.ok && (
                            <div className="mt-3 text-xs" style={{ color: "#5b6c78" }}>
                                <strong>Cosa fare:</strong>{" "}
                                {String(lastResult.message).toLowerCase().includes("blacklist") ||
                                String(lastResult.message).toLowerCase().includes("554") ? (
                                    <>L'IP del server è in DNSBL. Aruba sta rifiutando la connessione.
                                    Contatta il supporto Emergent o configura un servizio SMTP relay alternativo.</>
                                ) : String(lastResult.message).toLowerCase().includes("authentication") ||
                                  String(lastResult.message).toLowerCase().includes("535") ? (
                                    <>Credenziali SMTP errate. Verifica `SMTP_USER` e `SMTP_PASS` su Aruba e
                                    nell'ambiente di produzione.</>
                                ) : String(lastResult.message).toLowerCase().includes("non configurato") ? (
                                    <>Le variabili d'ambiente SMTP non sono impostate sul server di produzione.
                                    Devono essere aggiunte dal pannello di deploy Emergent prima di funzionare.</>
                                ) : (
                                    <>Leggi il messaggio di errore qui sopra e verifica la configurazione SMTP/DNS.</>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Tips */}
            <div className="bg-magni-cream border border-magni-light p-6 text-sm">
                <h3 className="font-serif-display text-xl text-magni-navy mb-3">Email che finiscono in spam?</h3>
                <p className="text-magni-muted mb-3">
                    Se l'invio riesce ma le email finiscono in spam (specialmente su Gmail/Outlook), configura questi
                    record DNS sul dominio <code className="bg-white px-1 border border-magni-light">magniflexbooking.com</code>{" "}
                    dal pannello Aruba:
                </p>
                <ul className="space-y-2 text-magni-navy">
                    <li>
                        <strong>SPF</strong> (TXT record sulla root):
                        <br />
                        <code className="bg-white px-2 py-1 border border-magni-light text-xs block mt-1">
                            v=spf1 include:_spf.aruba.it ~all
                        </code>
                    </li>
                    <li className="mt-3">
                        <strong>DKIM</strong>: dal pannello Aruba vai a <em>"PEC e Mail Pro &rarr; Impostazioni avanzate &rarr; Firma DKIM"</em>{" "}
                        e abilita la firma automatica per <code className="bg-white px-1 border border-magni-light">booking@magniflexbooking.com</code>.
                    </li>
                    <li className="mt-3">
                        <strong>DMARC</strong> (opzionale, dopo SPF+DKIM):
                        <br />
                        <code className="bg-white px-2 py-1 border border-magni-light text-xs block mt-1">
                            v=DMARC1; p=none; rua=mailto:booking@magniflexbooking.com
                        </code>
                    </li>
                </ul>
            </div>
        </div>
    );
}
